import type { DeviceInventoryDisplayStatus } from '@/lib/api/orpc/contracts/it-operation-core-device-inventory';

/**
 * デバイス棚卸しの「進行状況」の語彙（Figma のコンポーネント `デバイス棚卸し/進行状況` の
 * 4 バリアント）。列のバッジと束ね確認画面のフィルタが**この 1 つの語彙を共有する**。
 *
 * BE の `displayStatus`（7 値）をここへ集約する。`*_BEFORE` の 3 値は BOE の
 * ステップ遷移が非同期（SQS）なせいで一瞬だけ観測される中間状態で、運用上は
 * 次工程の待ち状態と区別がつかない。
 *
 * 🔴 集約を BE に持ち込まないこと。これは Figma のバッジの都合（表示の関心）であり、
 * `displayStatus` は既に一覧行に載っているので FE で潰せる。対して束ねのタブ（`tab`）は
 * FormData と Instance 状態を組み合わせた業務判定で行の列からは再現できないため、
 * BE 側に SSOT（`classifyDeviceInventoryTab`）がある。判定の所在が違う。
 */
export type DeviceInventoryProgressKind = 'notRequested' | 'notAnswered' | 'inReview' | 'completed';

/**
 * フィルタの選択肢の並び。ワークフロー順（依頼前 → 未回答 → 内容確認 → 完了）で、
 * `Object.keys` 順に依存しないよう明示的に持つ。
 */
export const DEVICE_INVENTORY_PROGRESS_KINDS = ['notRequested', 'notAnswered', 'inReview', 'completed'] as const satisfies readonly DeviceInventoryProgressKind[];

/**
 * `displayStatus`（7 値）→ 進行状況（4 値）。
 *
 * 集約後の 4 値は束ね画面のタブとほぼ 1:1 で対応する。ズレるのは 1 点だけ:
 *
 * | タブ | 進行状況 |
 * | --- | --- |
 * | 依頼前（`notRequested`） | 依頼前 |
 * | 未回答（`notAnswered`） | 未回答 |
 * | 正常・異常（`noActionRequired` / `actionRequired`） | **内容確認**（合流） |
 * | 完了（`completed`） | 完了 |
 *
 * - タブ「正常 / 異常」はどちらも確認待ちなので `inReview` に合流する。
 *   行単位の正常 / 異常は「所持確認」「状態報告」列が表す
 */
export const PROGRESS_KIND_BY_DISPLAY_STATUS: Record<DeviceInventoryDisplayStatus, DeviceInventoryProgressKind> = {
  REQUEST_BEFORE: 'notRequested',
  REQUEST_IN_PROGRESS: 'notRequested',
  SELF_REPORT_BEFORE: 'notAnswered',
  SELF_REPORT_IN_PROGRESS: 'notAnswered',
  REVIEW_BEFORE: 'inReview',
  REVIEW_IN_PROGRESS: 'inReview',
  REVIEW_COMPLETED: 'completed'
};

/**
 * 進行状況（4 値）→ `displayStatus`（7 値）。フィルタが API へ送る値の展開表。
 *
 * 🔴 手で書かないこと。{@link PROGRESS_KIND_BY_DISPLAY_STATUS} を畳んで導出することで、
 * 正写像に値を足したときの取りこぼしが構造的に起こらないようにしている。
 * 初期値の 4 キーは `Record<DeviceInventoryProgressKind, ...>` で縛られるので、
 * kind が増減したらコンパイルエラーになる。
 */
export const DISPLAY_STATUSES_BY_PROGRESS_KIND = Object.entries(PROGRESS_KIND_BY_DISPLAY_STATUS).reduce<Record<DeviceInventoryProgressKind, DeviceInventoryDisplayStatus[]>>((acc, [displayStatus, kind]) => {
  acc[kind].push(displayStatus as DeviceInventoryDisplayStatus);
  return acc;
}, {
  notRequested: [],
  notAnswered: [],
  inReview: [],
  completed: []
});

/**
 * URL の進行状況（4 値）を一覧 API の `displayStatuses`（7 値）へ展開する。
 *
 * 未選択（`undefined`）と空配列はどちらも「絞り込まない」意味なので `undefined` を返す
 * （BE の `z.array().optional()` は空配列を「0 件に絞る」とは解釈しないが、
 * クエリに空の bracket を載せる意味も無い）。
 */
export function toDeviceInventoryDisplayStatuses(kinds: readonly DeviceInventoryProgressKind[] | undefined): DeviceInventoryDisplayStatus[] | undefined {
  if (kinds === undefined || kinds.length === 0) {
    return undefined;
  }
  return kinds.flatMap(kind => DISPLAY_STATUSES_BY_PROGRESS_KIND[kind]);
}